import { Button, Heading } from "@/design-system";
import { usePublishedPosts } from "@/hooks/blog";
import type { BlogPostSummary } from "@/shared/blog/blogTypes";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { PostCard } from "./PostCard";

interface RelatedPostsProps {
  currentSlug: string;
  category: string | null;
  /** Cluster do post atual. Quando existe, ele manda na escolha dos vizinhos. */
  clusterId?: string | null;
}

/**
 * Tamanho da página pedida para escolher os vizinhos.
 *
 * `ListPublishedParams` (contrato do serviço, fora do escopo desta tela) não
 * tem filtro por cluster, então a alternativa seria uma requisição por
 * critério. Pedir uma página maior e separar no cliente resolve com **uma**
 * chamada: o acervo publicado é pequeno e o custo de 24 resumos é menor que o
 * de vários round-trips na página que o leitor já está lendo.
 */
const POOL_SIZE = 24;
/** Três telas de carrossel no desktop (3 cards cada). */
const MAX_RELATED = 9;

/** Mesmo `gap-6` da faixa — entra na conta do passo das setas. */
const GAP_PX = 24;

/**
 * "Continue lendo" ao pé do post, em carrossel.
 *
 * A ordem não é decorativa: posts do mesmo cluster vêm primeiro porque é esse
 * link lateral que transforma sete rótulos soltos em estrutura de tema — é o
 * que faz o buscador (e o leitor) entenderem que aquilo forma um assunto, e
 * não uma lista cronológica. Depois vêm os da mesma categoria e, por fim, os
 * mais recentes, para o carrossel não ficar com uma tela só.
 *
 * O carrossel é a própria rolagem horizontal do navegador com `scroll-snap`:
 * funciona no toque e no trackpad sem JavaScript, e as setas só pedem uma
 * rolagem do tamanho de um card. Os cards mantêm a largura da grade de antes
 * (3 por linha no desktop, 2 no tablet); no celular aparece a borda do
 * próximo card, que é o que diz "tem mais para o lado".
 */
export const RelatedPosts = ({ currentSlug, category, clusterId }: RelatedPostsProps) => {
  const { data } = usePublishedPosts({ pageSize: POOL_SIZE });
  const trackId = useId();
  const trackRef = useRef<HTMLUListElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const candidates = (data?.items ?? []).filter(p => p.slug !== currentSlug);
  const sameCluster = clusterId ? candidates.filter(p => p.clusterId === clusterId) : [];
  const sameCategory = candidates.filter(
    p => category && p.category === category && !sameCluster.includes(p)
  );
  const others = candidates.filter(p => !sameCluster.includes(p) && !sameCategory.includes(p));
  const related: BlogPostSummary[] = [...sameCluster, ...sameCategory, ...others].slice(
    0,
    MAX_RELATED
  );

  // Setas desabilitadas nas pontas. Tolerância de 1px: com zoom do navegador o
  // `scrollLeft` vem fracionado e nunca bate exatamente no fim.
  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanPrev(track.scrollLeft > 1);
    setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateArrows();
    const observer = new ResizeObserver(updateArrows);
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateArrows, related.length]);

  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    const card = track?.firstElementChild as HTMLElement | null;
    if (!track || !card) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollBy({
      left: direction * (card.offsetWidth + GAP_PX),
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  if (related.length === 0) return null;

  const hasOverflow = canPrev || canNext;

  return (
    <section aria-labelledby="related-title" className="border-ds-border mt-12 border-t pt-10">
      <div className="mb-6 flex items-center justify-between gap-4">
        <Heading level={2} size={3} id="related-title">
          Continue lendo
        </Heading>
        {hasOverflow && (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="secondary"
              size="sm"
              shape="circle"
              aria-label="Posts anteriores"
              aria-controls={trackId}
              disabled={!canPrev}
              onClick={() => scrollByCard(-1)}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              shape="circle"
              aria-label="Próximos posts"
              aria-controls={trackId}
              disabled={!canNext}
              onClick={() => scrollByCard(1)}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        )}
      </div>

      <ul
        id={trackId}
        ref={trackRef}
        onScroll={updateArrows}
        className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] motion-reduce:scroll-auto [&::-webkit-scrollbar]:hidden"
      >
        {related.map(post => (
          <li
            key={post.id}
            className="shrink-0 basis-[85%] snap-start sm:basis-[calc((100%-1.5rem)/2)] lg:basis-[calc((100%-3rem)/3)]"
          >
            <PostCard post={post} />
          </li>
        ))}
      </ul>
    </section>
  );
};
